import { NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, Building2, ArrowLeft, LogOut, Package, ShieldCheck } from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { useAuth } from '@/contexts/auth-context';
import { getAppBaseUrl, getAppLoginUrl } from '@/lib/app-url';

const navItems = [
  { to: '/platform-admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/platform-admin/organizations', label: 'Organizations', icon: Building2 },
  { to: '/platform-admin/security', label: 'Security', icon: ShieldCheck },
  { to: '/platform/gear-models/custom', label: 'Gear Models', icon: Package },
];

function SidebarLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
            : 'text-gray-600 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-accent hover:text-gray-900 dark:hover:text-foreground'
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

export function PlatformAdminLayout() {
  const { logout } = useAuth();

  const handleLogout = async () => {
    if (await logout()) window.location.href = getAppLoginUrl();
  };

  const appDashboardUrl = `${getAppBaseUrl()}/dashboard`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 dark:border-border bg-white dark:bg-card px-4 pb-4">
          {/* Header */}
          <div className="flex h-16 shrink-0 items-center border-b border-gray-100 dark:border-border">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white text-xs font-bold">
                H3
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-foreground">Platform Admin</p>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">H3ight Staff</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col gap-1">
            {navItems.map((item) => (
              <SidebarLink key={item.to} {...item} />
            ))}
          </nav>

          {/* Back to App + Logout */}
          <div className="border-t border-gray-100 dark:border-border pt-4 space-y-1">
            <a
              href={appDashboardUrl}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-accent hover:text-gray-700 dark:hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to App
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-accent hover:text-gray-700 dark:hover:text-foreground transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="sticky top-0 z-40 flex items-center gap-x-4 border-b border-gray-200 dark:border-border bg-white dark:bg-card px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600 text-white text-xs font-bold">
            H3
          </div>
          <span className="text-sm font-semibold">Platform Admin</span>
        </div>
        <div className="flex-1" />
        <a href={appDashboardUrl} className="text-xs text-blue-600 hover:underline">
          Back to App
        </a>
        <button
          type="button"
          onClick={handleLogout}
          className="text-xs text-blue-600 hover:underline"
        >
          Log out
        </button>
      </div>

      {/* Mobile Nav */}
      <div className="border-b border-gray-200 dark:border-border bg-white dark:bg-card px-4 lg:hidden">
        <nav className="flex gap-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 dark:text-muted-foreground hover:border-gray-300 dark:hover:border-muted-foreground hover:text-gray-700 dark:hover:text-foreground'
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
